import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import moment from 'moment';
import { MmRouletteModalComponent } from 'src/app/components/mm-roulette-modal/mm-roulette-modal.component';
import { Roulette, RouletteFreePlays, RouletteGameResponse, RouletteResult } from 'src/app/models/roulette.model';
import { ModelLocatorService } from '../config/model-locator.service';
import { MessageService } from '../message-service/message.service';
import { MultiluckService } from '../multiluck-service/multiluck.service';
import { ProfileService } from '../profile-service/profile.service';

/**
 * @author Starley Cazorla
 */
@Injectable({
    providedIn: 'root'
})
export class RouletteService {

    private shown: boolean = false;

    constructor(
        private modalCtrl: ModalController,
        private multiluckService: MultiluckService,
        private modelLocator: ModelLocatorService,
        private messageService: MessageService,
        private profileService: ProfileService
    ) { }

    shouldShow(): boolean {
        return !this.shown;
    }

    markShown(): void {
        this.shown = true;
    }

    reset(): void {
        this.shown = false;
    }

    async getRoulettePrizes(id_game: number): Promise<RouletteResult | null> {
        try {
            const resultRoulette = await this.multiluckService.rescueRoulette(this.modelLocator.dadosLogin.cpf, this.modelLocator.dadosLogin.idowner, id_game);
            if (resultRoulette && resultRoulette.status === 200 && resultRoulette.result) {
                return resultRoulette;
            }
            return null;
        } catch (error: any) {
            const errorMessage = error.error?.message || 'Erro ao girar a roleta!';
            this.messageService.showToast(errorMessage, 'danger', 'bottom', 3000, true);
            return null;
        }
    }

    async checkRoulette(): Promise<void> {
        if (!this.shouldShow()) {
            return;
        }

        const resultRoulette = await this.getPlayableRoulette();

        if (!resultRoulette) {
            return;
        }

        this.markShown();
        await this.showModalRoulette(resultRoulette);
    }

    async getPlayableRoulette(): Promise<Roulette | null> {
        try {
            const roulette = await this.getRouletteActive();
            return roulette?.canSpin && roulette.prizes.length ? roulette : null;
        } catch (error) {
            return null;
        }
    }

    async getRouletteActive(): Promise<Roulette | null> {
        const res: any = await this.multiluckService.getListOfGames('roleta', this.modelLocator.dadosLogin.cpf);

        if (res?.status != 200 || !Array.isArray(res.result)) {
            return null;
        }

        const game: RouletteGameResponse | undefined = res.result.find((g: RouletteGameResponse) => g.jogo_ativo);

        if (!game) {
            return null;
        }

        const freePlays: RouletteFreePlays | undefined = res.jogadasGratuitas?.find((plays: RouletteFreePlays) => plays.game === 'roleta');
        const availableSpins = freePlays?.jogadas ?? 0;

        if (!freePlays) {
            return null;
        }

        return {
            id_jogo: game.id_jogo,
            canSpin: game.jogo_ativo && availableSpins > 0,
            availableSpins: availableSpins,
            title: game.descricao,
            message: game.mensagem,
            buttonText: game.botao_texto,
            badgeText: game.badge_texto,
            prizes: (game.imagens_jogos ?? [])
                .map(prize => ({ ...prize, winner: false }))
                .sort((a, b) => a.ordem - b.ordem)
        };
    }

    async showModalRoulette(roulette: Roulette | null) {
        if (!roulette) {
            return;
        }

        const modal = await this.modalCtrl.create({
            component: MmRouletteModalComponent,
            componentProps: {
                gameId: roulette.id_jogo,
                canSpin: roulette.canSpin,
                availableSpins: roulette.availableSpins,
                title: roulette.title,
                message: roulette.message,
                buttonText: roulette.buttonText,
                badgeText: roulette.badgeText,
                prizes: roulette.prizes
            },
            cssClass: 'roulette-modal-transparent',
            backdropDismiss: false
        });

        await modal.present();

    }

    getUserEmail(): string {
        return this.modelLocator.dadosLogin.email ?? '';
    }

    async updateUserEmail(email: string): Promise<boolean> {
        try {
            const profileResult: any = await this.profileService.getAllDataProfile();
            const profile = profileResult.result;
            const res: any = await this.profileService.updateProfile({
                cpf: this.modelLocator.dadosLogin.cpf,
                nome: profile.nome,
                nascimento: moment(profile.nascimento, 'YYYY-MM-DD').toISOString(),
                celular: String(profile.celular ?? '').replace(/[^\d]+/g, ''),
                email: email
            });

            if (res?.status !== 200) {
                this.messageService.showToast(res?.message || 'Erro ao alterar o e-mail!', 'warning', 'bottom', 5000, true);
                return false;
            }

            this.modelLocator.dadosLogin.email = email;
            localStorage.setItem('AUTH', JSON.stringify(this.modelLocator.dadosLogin));
            return true;
        } catch (error: any) {
            const errorMessage = error.error?.message || 'Erro ao alterar o e-mail!';
            this.messageService.showToast(errorMessage, 'danger', 'bottom', 3000, true);
            return false;
        }
    }
}
